// RewardNotificationOverlay.jsx
// Modal emergente para la modelo cuando recibe una recompensa de admin.
// Pollinga /api/users/me/pending-rewards cada 45s y muestra un overlay hasta que la modelo cierre.
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 5_000;

export function RewardNotificationOverlay() {
  const { user } = useAuth();
  const [queue, setQueue]     = useState([]);
  const [closing, setClosing] = useState(false);
  const timerRef              = useRef(null);

  const isCreator = user?.role === "creator" || user?.role === "model";

  const fetchPending = useCallback(async () => {
    if (!user || !isCreator) return;
    try {
      const res = await axios.get(`${API}/api/users/me/pending-rewards`, { withCredentials: true });
      setQueue(res.data || []);
    } catch {
      /* silencioso */
    }
  }, [user, isCreator]);

  useEffect(() => {
    fetchPending();
    timerRef.current = setInterval(fetchPending, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchPending]);

  const current = queue[0] || null;

  const handleClose = async () => {
    if (!current || closing) return;
    setClosing(true);
    try {
      await axios.post(
        `${API}/api/users/me/rewards/${current.id}/ack`,
        {},
        { withCredentials: true },
      );
    } catch { /* silencioso */ }
    setQueue(prev => prev.slice(1));
    setClosing(false);
  };

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key={current.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
          data-testid="reward-overlay"
        >
          <motion.div
            initial={{ scale: 0.88, y: 24 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.88, y: 24, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 280 }}
            className="relative w-full max-w-sm rounded-2xl overflow-hidden border border-amber-500/30"
            style={{
              background: "linear-gradient(145deg, rgba(100,50,0,0.72) 0%, rgba(18,12,0,0.92) 100%)",
              boxShadow: "0 0 48px 4px rgba(245,158,11,0.22), 0 0 0 1px rgba(245,158,11,0.12)",
            }}
          >
            {/* Franja dorada superior */}
            <div
              className="h-1 w-full"
              style={{ background: "linear-gradient(90deg, #b45309, #fbbf24, #b45309)" }}
            />

            <div className="px-6 pt-6 pb-7 flex flex-col items-center gap-4 text-center">
              {/* Icono */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center border border-amber-400/30"
                style={{ background: "rgba(245,158,11,0.15)" }}
              >
                <Sparkles className="w-5 h-5 text-amber-300" />
              </div>

              {/* Título */}
              <p className="text-amber-200/80 text-xs font-heading font-semibold tracking-widest uppercase">
                StarTip te ha enviado
              </p>

              {/* Importe */}
              <p
                className="font-heading font-black text-5xl tracking-tight leading-none"
                style={{ color: "#fbbf24", textShadow: "0 0 24px rgba(251,191,36,0.50)" }}
                data-testid="reward-amount"
              >
                {Number(current.amount || 0).toLocaleString("es-ES", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} €
              </p>

              {/* Mensaje */}
              {current.message && (
                <p
                  className="text-zinc-300 text-sm font-body leading-relaxed italic px-2"
                  data-testid="reward-message"
                >
                  "{current.message}"
                </p>
              )}

              {/* Botón cerrar */}
              <button
                onClick={handleClose}
                disabled={closing}
                data-testid="reward-close-btn"
                className="mt-2 w-full py-3 rounded-xl font-heading font-bold text-sm tracking-wide uppercase transition-all active:scale-95"
                style={{
                  background: closing ? "rgba(245,158,11,0.10)" : "rgba(245,158,11,0.20)",
                  border: "1px solid rgba(245,158,11,0.40)",
                  color: "#fcd34d",
                }}
              >
                {closing ? "Cerrando…" : "Cerrar"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
